import { createClient } from '@supabase/supabase-js'

export const supabase = createClient(
  import.meta.env.VITE_SUPABASE_URL,
  import.meta.env.VITE_SUPABASE_ANON_KEY,
)

// Chaves que são sincronizadas com o Supabase via app_kv.
// NOTA: 'mini_pedidos' SAIU dessa lista a partir de 2026-10-30.
// Agora pedidos moram na tabela dedicada `pedidos` (uma linha por
// pedido), e são sincronizados por supabase-pedidos.js. Isso elimina
// a classe de bugs onde um device com cache antigo sobrescrevia o
// array inteiro no upsert. Os outros arrays (tags, oportunidades,
// marketplace_remessas, bling_pedidos_ocultos) ficaram em app_kv
// porque são menores e usam `syncKeyWithMerge` com merge por id —
// que já cobre o risco pra esses volumes.
const SYNC_KEYS = new Set([
  'mini_tags',
  'mini_config',
  // Tabela de preços — fonte compartilhada com a Vendedora IA (Mila).
  // Mila chama /api/calcular-orcamento que lê daqui pros multiplicadores e
  // preços de adicionais/puxadores.
  'mini_tabela_precos',
  'mini_marketplace_pagamentos',
  'mini_marketplace_remessas',
  // Oportunidades Arco — leads qualificados pra a Arco Marcenaria (móveis
  // planejados), captados pelas vendedoras da Mini.
  'mini_oportunidades_arco',
  'bling_oauth_tokens',
  'bling_pedidos_status',
  'bling_pedidos_logistica',
  'bling_pedidos_ocultos',
  // Dados do Bling sincronizados pelo cron automático
  'bling_historico',
  'bling_historico_meta',
  'bling_pedidos_detalhes',
  'bling_contatos',
  'bling_contatos_meta',
])

// Prefixo dos timestamps de última escrita local por chave. Guardamos
// pra saber se a versão local é mais fresca que a do Supabase quando
// vamos sincronizar de volta — sem isso, o sync sobrescrevia a
// alteração recém-salva com o valor antigo do Supabase (race condition
// clássica com upsert fire-and-forget).
const LOCAL_TS_PREFIX = 'mini_kv_ts_'

// Chaves de cache do Bling que podem ser descartadas quando o
// localStorage estoura (serão re-sincronizadas depois). Ordem = qual
// liberar primeiro (menos crítico pra UI → mais).
const BLING_CACHE_EVICTABLE = [
  'bling_contatos',
  'bling_contatos_meta',
  'bling_pedidos_detalhes',
  'bling_historico',
  'bling_historico_meta',
]

// setItem que resiste a QuotaExceededError: se estourar, limpa chaves
// de cache do Bling em ordem até conseguir salvar. Essencial pra
// mini_pedidos — sem isso, o sync falha silenciosamente e o localStorage
// fica desatualizado, abrindo caminho pra sobrescrever o Supabase na
// próxima edição.
export function safeSetItem(key, value) {
  try {
    localStorage.setItem(key, value)
    return true
  } catch (e) {
    if (!(e instanceof DOMException) || e.name !== 'QuotaExceededError') {
      throw e
    }
    console.warn(`[Supabase] QuotaExceededError gravando ${key}. Liberando cache do Bling...`)
    for (const evict of BLING_CACHE_EVICTABLE) {
      if (evict === key) continue
      if (localStorage.getItem(evict) === null) continue
      localStorage.removeItem(evict)
      try {
        localStorage.setItem(key, value)
        console.warn(`[Supabase] Liberou ${evict}, gravou ${key}`)
        return true
      } catch (retry) {
        if (!(retry instanceof DOMException) || retry.name !== 'QuotaExceededError') throw retry
      }
    }
    // Último recurso falhou. Marca pro diagnóstico e deixa estourar.
    try {
      localStorage.setItem('mini_last_sync_error',
        `QuotaExceededError gravando ${key} (${(value?.length || 0)} bytes) mesmo após limpar cache do Bling`)
    } catch { /* se nem o erro cabe, desiste */ }
    throw e
  }
}

// Carrega dados do Supabase no localStorage, mas preserva chaves onde
// o local é MAIS RECENTE que o remoto (a versão local venceria o
// upsert eventual).
export async function syncFromSupabase() {
  try {
    const { data, error } = await supabase
      .from('app_kv')
      .select('key, value, updated_at')
      .in('key', [...SYNC_KEYS])

    if (error) throw error

    // Prioriza chaves mini_* sobre bling_* — se o localStorage estourar,
    // o cache do Bling perde antes dos dados da equipe.
    const ordenado = [...(data || [])].sort((a, b) => {
      const aMini = a.key?.startsWith('mini_') ? 0 : 1
      const bMini = b.key?.startsWith('mini_') ? 0 : 1
      return aMini - bMini
    })

    let aplicadas = 0, preservadas = 0, puladas_por_quota = 0
    for (const { key, value, updated_at } of ordenado) {
      if (value === null) continue
      const localTs = localStorage.getItem(LOCAL_TS_PREFIX + key)
      // Se o local tem timestamp E é mais recente que o remoto, PULA —
      // significa que alguém salvou aqui e o upsert ainda pode não ter
      // chegado no Supabase. Deixa o sync do syncKey terminar de propagar.
      if (localTs && updated_at && localTs > updated_at) {
        preservadas++
        continue
      }
      try {
        safeSetItem(key, JSON.stringify(value))
      } catch (quotaErr) {
        // Chave do Bling que estourou mesmo depois de limpar: pula
        // (vai tentar de novo no próximo sync). Chave mini_* nunca
        // deve chegar aqui depois do fix, mas se chegar, re-throw
        // pra garantir que o erro apareça na tela de diagnóstico.
        if (key.startsWith('mini_')) throw quotaErr
        console.warn(`[Supabase] Pulando ${key} (quota exceeded persistente)`)
        puladas_por_quota++
        continue
      }
      // Sincroniza o timestamp local com o remoto pra próximas comparações
      if (updated_at) localStorage.setItem(LOCAL_TS_PREFIX + key, updated_at)
      aplicadas++
    }
    // Sincroniza também os pedidos (que agora moram na tabela
    // dedicada, não mais em app_kv.mini_pedidos).
    try {
      const { syncPedidosFromTabela } = await import('./supabase-pedidos')
      await syncPedidosFromTabela()
    } catch (e) {
      console.error('[Supabase] syncPedidosFromTabela error:', e)
    }

    // Registra timestamp + quantas chaves vieram, pra página de
    // diagnóstico mostrar quando foi o último sync bem-sucedido
    localStorage.setItem('mini_last_sync_at', new Date().toISOString())
    localStorage.setItem('mini_last_sync_count', String((data || []).length))
    if (preservadas > 0) {
      console.log(`[Sync] ${aplicadas} aplicadas, ${preservadas} preservadas (local mais recente)`)
    }
    return true
  } catch (e) {
    console.error('[Supabase] syncFromSupabase error:', e)
    localStorage.setItem('mini_last_sync_error', String(e?.message || e))
    return false
  }
}

// Envia TODOS os dados do localStorage para o Supabase (migração inicial)
export async function pushAllToSupabase() {
  const rows = []
  for (const key of SYNC_KEYS) {
    const raw = localStorage.getItem(key)
    if (raw === null) continue
    const value = (() => { try { return JSON.parse(raw) } catch { return raw } })()
    rows.push({ key, value, updated_at: new Date().toISOString() })
  }
  if (rows.length === 0) return { ok: true, count: 0 }
  const { error } = await supabase
    .from('app_kv')
    .upsert(rows, { onConflict: 'key' })
  if (error) throw error
  return { ok: true, count: rows.length }
}

// Envia uma chave do localStorage para o Supabase (fire-and-forget).
// IMPORTANTE: grava um timestamp local ANTES do upsert. O syncFromSupabase
// usa esse timestamp pra saber que essa chave foi escrita localmente há
// pouco e NÃO deve ser sobrescrita pelo valor remoto (que ainda pode ser
// antigo — o upsert é fire-and-forget e pode não ter chegado no Supabase).
// Variante do syncKey pra chaves que são ARRAY DE OBJETOS COM ID.
// Antes de empurrar o array local pro Supabase, baixa o remoto e
// mescla por id: pedidos que estão no remoto E não no local são
// PRESERVADOS no array final. Elimina o overwrite silencioso que
// aconteceu dia 18/09–22/10 (dispositivo com cache antigo sobrescrevia
// pedidos novos cadastrados por outro dispositivo).
//
// Uso: `savePedido`, `deletePedido`, `restaurarPedido` e
// `apagarPedidoDefinitivo` em storage.js — chamam esta variante no
// lugar de syncKey() quando a chave é 'mini_pedidos' (array de pedidos
// com id UUID).
//
// Comportamento:
// - Delete explícito (apagarPedidoDefinitivo): NÃO preserva o pedido
//   deletado se ele veio só do remoto. O id removido localmente também
//   é excluído do merged (via Set de ids pra remover).
// - Edição: pedido local vence (local é a nova versão).
// - Criação: pedido local é novo, não tem no remoto.
export async function syncKeyWithMerge(key, idsToRemove = new Set()) {
  if (!SYNC_KEYS.has(key)) return

  const raw = localStorage.getItem(key)
  const localValue = raw !== null ? (() => { try { return JSON.parse(raw) } catch { return raw } })() : null
  const now = new Date().toISOString()
  localStorage.setItem(LOCAL_TS_PREFIX + key, now)

  // Só mescla quando o valor é array. Pra outros tipos, cai no fluxo
  // antigo (overwrite — tudo bem porque não é array por id).
  if (!Array.isArray(localValue)) {
    return syncKey(key)
  }

  try {
    const { data, error } = await supabase.from('app_kv')
      .select('value')
      .eq('key', key)
      .single()

    let merged = localValue
    if (!error && Array.isArray(data?.value)) {
      // Suporta tanto arrays de objetos com id (mini_pedidos, mini_tags,
      // mini_oportunidades_arco, mini_marketplace_remessas) quanto arrays
      // de primitivos (bling_pedidos_ocultos — array de ids string).
      const primeiro = localValue[0] ?? data.value[0]
      const ehPrimitivo = primeiro !== null && typeof primeiro !== 'object'

      if (ehPrimitivo) {
        const localSet = new Set(localValue)
        const extras = data.value.filter(v =>
          v != null && !localSet.has(v) && !idsToRemove.has(v)
        )
        merged = [...extras, ...localValue]
      } else {
        const localIds = new Set(localValue.map(p => p?.id).filter(Boolean))
        const extras = data.value.filter(p =>
          p?.id &&
          !localIds.has(p.id) &&
          !idsToRemove.has(p.id)
        )
        merged = [...extras, ...localValue]
      }
    }

    // Grava o merged no local (recupera os extras do remoto) E no remoto
    safeSetItem(key, JSON.stringify(merged))

    const { error: upsertError } = await supabase.from('app_kv')
      .upsert({ key, value: merged, updated_at: now }, { onConflict: 'key' })

    if (upsertError) {
      console.error('[Supabase] upsertMerged error:', key, upsertError)
      localStorage.setItem('mini_last_write_error', JSON.stringify({
        key, op: 'upsertMerged', msg: upsertError?.message || String(upsertError), when: now,
      }))
    } else {
      localStorage.removeItem('mini_last_write_error')
    }
  } catch (e) {
    console.error('[Supabase] syncKeyWithMerge error:', key, e)
    localStorage.setItem('mini_last_write_error', JSON.stringify({
      key, op: 'syncKeyWithMerge', msg: String(e?.message || e), when: now,
    }))
    // IMPORTANTE: NÃO cair pra `syncKey` cego aqui. O fallback antigo
    // fazia upsert do array local sem merge — exatamente o bug que
    // destruiu 77 pedidos entre 18/09 e 22/10. O LOCAL_TS_PREFIX já foi
    // gravado no início desta função, então o syncFromSupabase vai
    // PRESERVAR a versão local até o próximo retry conseguir mergir e
    // enviar. Melhor atrasar a propagação que arriscar overwrite.
  }
}

export function syncKey(key) {
  if (!SYNC_KEYS.has(key)) return

  const raw = localStorage.getItem(key)
  const value = raw !== null ? (() => { try { return JSON.parse(raw) } catch { return raw } })() : null
  const now = new Date().toISOString()
  // Marca que essa chave foi escrita localmente AGORA — protege contra
  // syncFromSupabase sobrescrever antes do upsert propagar
  localStorage.setItem(LOCAL_TS_PREFIX + key, now)

  if (value === null) {
    supabase.from('app_kv').delete().eq('key', key).then(({ error }) => {
      if (error) {
        console.error('[Supabase] delete error:', key, error)
        localStorage.setItem('mini_last_write_error', JSON.stringify({ key, op: 'delete', msg: error?.message || String(error), when: now }))
      } else {
        // Sucesso: limpa erro anterior se houver
        localStorage.removeItem('mini_last_write_error')
      }
    })
  } else {
    supabase.from('app_kv')
      .upsert({ key, value, updated_at: now }, { onConflict: 'key' })
      .then(({ error }) => {
        if (error) {
          console.error('[Supabase] upsert error:', key, error)
          localStorage.setItem('mini_last_write_error', JSON.stringify({ key, op: 'upsert', msg: error?.message || String(error), when: now }))
        } else {
          localStorage.removeItem('mini_last_write_error')
        }
      })
  }
}
